import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { campaignService } from '@/services/altingross';

// ─── Status enum ───────────────────────────────────────────────────────────
export const CampaignStatus = {
  0: 'Taslak',
  1: 'Gönderildi',
  2: 'Gönderiliyor',
  3: 'İptal Edildi',
};

export const CampaignStatusValue = {
  Taslak:        0,
  Gönderildi:    1,
  Gönderiliyor:  2,
  İptalEdildi:   3,
};

// ─── Thunks ────────────────────────────────────────────────────────────────
export const fetchCampaigns = createAsyncThunk(
  'campaigns/fetchList',
  async ({ page = 1, size = 10, filter = null } = {}, { rejectWithValue }) => {
    try {
      const res = await campaignService.getList(page, size, filter);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const fetchCampaign = createAsyncThunk(
  'campaigns/fetchOne',
  async (id, { rejectWithValue }) => {
    try {
      const res = await campaignService.getById(id);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const updateCampaign = createAsyncThunk(
  'campaigns/update',
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const res = await campaignService.update(id, body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const deleteCampaignById = createAsyncThunk(
  'campaigns/delete',
  async (id, { rejectWithValue }) => {
    try {
      await campaignService.deleteById(id);
      return id;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const generateAiTemplate = createAsyncThunk(
  'campaigns/generateAiTemplate',
  async (prompt, { rejectWithValue }) => {
    try {
      const res = await campaignService.createTemplateWithAi(prompt);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const createCampaign = createAsyncThunk(
  'campaigns/create',
  async (body, { rejectWithValue }) => {
    try {
      const res = await campaignService.create(body);
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

export const sendCampaignMail = createAsyncThunk(
  'campaigns/sendMail',
  async (id, { rejectWithValue }) => {
    try {
      const res = await campaignService.sendMail(id);
      return res.data; // { id, status }
    } catch (err) {
      return rejectWithValue(err?.response?.data ?? err.message);
    }
  }
);

// ─── Slice ─────────────────────────────────────────────────────────────────
const campaignsSlice = createSlice({
  name: 'campaigns',
  initialState: {
    list: {
      data:    null,  // { page, size, count, hasNext, hasPrev, totalPages, items[] }
      loading: false,
      error:   null,
    },
    detail: {
      data:    null,
      loading: false,
      error:   null,
    },
    aiLoading:     false,
    aiError:       null,
    saveLoading:   false,
    saveError:     null,
    updateLoading: false,
    updateError:   null,
    deleteLoading: false,
    deleteError:   null,
    deletingId:    null,
    sendLoading:   false,
    sendError:     null,
    sendingId:     null,
    lastCreatedId: null,
  },
  reducers: {
    resetDetail: (state) => {
      state.detail = { data: null, loading: false, error: null };
    },
    resetList: (state) => {
      state.list = { data: null, loading: false, error: null };
    },
    resetCampaignState: (state) => {
      state.aiLoading    = false;
      state.aiError      = null;
      state.saveLoading  = false;
      state.saveError    = null;
      state.sendLoading  = false;
      state.sendError    = null;
      state.sendingId    = null;
      state.lastCreatedId = null;
    },
  },
  extraReducers: (builder) => {
    // fetchCampaigns
    builder
      .addCase(fetchCampaigns.pending, (state) => {
        state.list.loading = true;
        state.list.error   = null;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data    = action.payload;
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error   = action.payload;
      });

    // fetchCampaign
    builder
      .addCase(fetchCampaign.pending, (state) => {
        state.detail.loading = true;
        state.detail.error   = null;
      })
      .addCase(fetchCampaign.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.data    = action.payload;
      })
      .addCase(fetchCampaign.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error   = action.payload;
      });

    // updateCampaign
    builder
      .addCase(updateCampaign.pending, (state) => {
        state.updateLoading = true;
        state.updateError   = null;
      })
      .addCase(updateCampaign.fulfilled, (state) => {
        state.updateLoading = false;
      })
      .addCase(updateCampaign.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError   = action.payload;
      });

    // deleteCampaignById
    builder
      .addCase(deleteCampaignById.pending, (state, action) => {
        state.deleteLoading = true;
        state.deleteError   = null;
        state.deletingId    = action.meta.arg;
      })
      .addCase(deleteCampaignById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.deletingId    = null;
        if (state.list.data?.items) {
          state.list.data.items = state.list.data.items.filter(i => i.id !== action.payload);
          state.list.data.count = Math.max(0, (state.list.data.count ?? 1) - 1);
        }
      })
      .addCase(deleteCampaignById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError   = action.payload;
        state.deletingId    = null;
      });

    // generateAiTemplate
    builder
      .addCase(generateAiTemplate.pending, (state) => {
        state.aiLoading = true;
        state.aiError   = null;
      })
      .addCase(generateAiTemplate.fulfilled, (state) => {
        state.aiLoading = false;
      })
      .addCase(generateAiTemplate.rejected, (state, action) => {
        state.aiLoading = false;
        state.aiError   = action.payload;
      });

    // createCampaign
    builder
      .addCase(createCampaign.pending, (state) => {
        state.saveLoading = true;
        state.saveError   = null;
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.saveLoading   = false;
        state.lastCreatedId = action.payload?.id ?? null;
      })
      .addCase(createCampaign.rejected, (state, action) => {
        state.saveLoading = false;
        state.saveError   = action.payload;
      });

    // sendCampaignMail
    builder
      .addCase(sendCampaignMail.pending, (state, action) => {
        state.sendLoading = true;
        state.sendError   = null;
        state.sendingId   = action.meta.arg;
      })
      .addCase(sendCampaignMail.fulfilled, (state, action) => {
        state.sendLoading = false;
        state.sendingId   = null;
        const { id, status } = action.payload ?? {};
        if (id !== undefined && status !== undefined && state.list.data?.items) {
          state.list.data.items = state.list.data.items.map(i =>
            i.id === id ? { ...i, status } : i
          );
        }
      })
      .addCase(sendCampaignMail.rejected, (state, action) => {
        state.sendLoading = false;
        state.sendError   = action.payload;
        state.sendingId   = null;
      });
  },
});

export const { resetDetail, resetList, resetCampaignState } = campaignsSlice.actions;
export default campaignsSlice.reducer;
